import { jsonInit, toSearchParams } from '@shigo-ui/api-client';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  campaignSchema,
  campaignsPageSchema,
  lineItemSchema,
  lineItemsPageSchema,
  serializeCampaignsFilter,
  serializeLineItemsFilter,
  type CampaignPayload,
  type CampaignsFilter,
  type LineItemsFilter,
} from './model';
import type {
  MadBuyConversionPixel,
  MadBuyCreative,
  MadBuyCreativeType,
  MadBuyLineItemMutationPayload,
} from './lineItemsCoreModel';
import {
  madBuyLineItemSchema,
  pagedConversionPixelsSchema,
  pagedCreativesSchema,
} from './lineItemSchemas';
import {
  lineItemTargetingCsvImportSchema,
  type LineItemTargetingCsvKind,
} from './lineItemTargetingCsvModel';

type CampaignsClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
  demand?: boolean;
};

export function createCampaignsClient({
  config,
  accessToken,
  fetcher,
  demand = false,
}: CampaignsClientOptions) {
  const prefix = demand ? '' : '/campaign';
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    listCampaigns: (filter: CampaignsFilter) =>
      api.request({
        path: `${prefix}/campaigns?${serializeCampaignsFilter(filter)}`,
        schema: campaignsPageSchema,
      }),
    getCampaign: (id: string) =>
      api.request({
        path: `${prefix}/campaigns/${encodeURIComponent(id)}`,
        schema: campaignSchema,
      }),
    createCampaign: (payload: CampaignPayload) =>
      api.request({
        path: `${prefix}/campaigns/${encodeURIComponent(payload.brandId)}`,
        schema: campaignSchema,
        init: jsonInit('POST', payload),
      }),
    updateCampaign: (id: string, payload: CampaignPayload) =>
      api.request({
        path: `${prefix}/campaigns/${encodeURIComponent(id)}`,
        schema: campaignSchema,
        init: jsonInit('PUT', payload),
      }),
    duplicateCampaign: (id: string, deepCopyCreatives: boolean) => {
      const params = new URLSearchParams({
        deepCopyCreatives: String(deepCopyCreatives),
        deepCopyLineItems: 'true',
      });
      return api.request({
        path: `${prefix}/campaigns/${encodeURIComponent(id)}/duplicate?${params}`,
        schema: campaignSchema,
        init: { method: 'POST' },
      });
    },
    listLineItems: (filter: LineItemsFilter) =>
      api.request({
        path: `${prefix}/line-items?${serializeLineItemsFilter(filter)}`,
        schema: lineItemsPageSchema,
      }),
    getLineItem: (id: string) =>
      api.request({
        path: `${prefix}/line-items/${encodeURIComponent(id)}`,
        schema: madBuyLineItemSchema,
      }),
    createLineItem: (payload: MadBuyLineItemMutationPayload) =>
      api.request({
        path: `${prefix}/line-items`,
        schema: madBuyLineItemSchema,
        init: jsonInit('POST', payload),
      }),
    updateLineItem: (id: string, payload: MadBuyLineItemMutationPayload) =>
      api.request({
        path: `${prefix}/line-items/${encodeURIComponent(id)}`,
        schema: madBuyLineItemSchema,
        init: jsonInit('PUT', payload),
      }),
    duplicateLineItem: (id: string, deepCopyCreatives: boolean) =>
      api.request({
        path: `${prefix}/line-items/${encodeURIComponent(id)}/duplicate?${new URLSearchParams({ deepCopyCreatives: String(deepCopyCreatives) })}`,
        schema: lineItemSchema,
        init: { method: 'POST' },
      }),
    importLineItemTargetingCsv: (id: string, kind: LineItemTargetingCsvKind, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.request({
        path: `${prefix}/line-items/${encodeURIComponent(id)}/targeting/${kind}/import`,
        schema: lineItemTargetingCsvImportSchema,
        // The browser sets the multipart Content-Type with its boundary.
        init: { method: 'POST', body },
      });
    },
    exportLineItemTargetingCsv: (id: string, kind: LineItemTargetingCsvKind) =>
      api.requestBlob({
        path: `${prefix}/line-items/${encodeURIComponent(id)}/targeting/${kind}/export`,
        init: { headers: { Accept: 'text/csv, application/json' }, cache: 'no-store' },
      }),
    listConversionPixels: async (params: {
      brandId: string;
      page?: number;
      size?: number;
      sortProperty?: readonly string[];
      sortDirection?: 'ASC' | 'DESC';
    }): Promise<MadBuyConversionPixel[]> => {
      const page = await api.request({
        path: `/conversion-pixels?${toSearchParams(params)}`,
        schema: pagedConversionPixelsSchema,
      });
      return page.content;
    },
    listCreatives: async (params: {
      brandId: string;
      type: MadBuyCreativeType;
      page?: number;
      size?: number;
    }): Promise<MadBuyCreative[]> => {
      const { brandId, ...query } = params;
      const page = await api.request({
        path: `${prefix}/creatives/${encodeURIComponent(brandId)}?${toSearchParams(query)}`,
        schema: pagedCreativesSchema,
      });
      return page.content;
    },
  };
}
